<template>
  <div>
    <b-row>
      <b-col md="6">
        <b-form-group label="Filo adı">
          <b-form-input :value="value.name" maxlength="120" @input="set('name', $event)" />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Bütçe (USDT)" :description="lockText('budget', 'Test: 3400 USDT. Sonuçlar bütçeyle orantılı ölçeklenir.')">
          <b-form-input type="number" min="100" step="100" :value="value.budget" :disabled="isLocked('budget')" @input="setNum('budget', $event)" />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Slot sayısı" :description="lockText('slotCount', `Test: 12 · slot başı ${perSlot} USDT`)">
          <b-form-input type="number" min="1" max="30" :value="value.slotCount" :disabled="isLocked('slotCount')" @input="setInt('slotCount', $event)" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-group label="Kaldıraç" :description="lockText('leverage', '4x önerilen başlangıç, 5x koşullu hedef. 8x ve üstü kullanılmaz.')">
      <div class="d-flex flex-wrap" style="gap: 0.5rem;">
        <b-button
          v-for="l in leverages"
          :key="l"
          size="sm"
          :disabled="isLocked('leverage')"
          :variant="value.leverage === l ? leverageVariant(l) : `outline-${leverageVariant(l)}`"
          @click="set('leverage', l)"
        >
          {{ l }}x<small v-if="l === 4"> · önerilen</small><small v-else-if="l === 5"> · koşullu hedef</small>
        </b-button>
      </div>
    </b-form-group>

    <b-form-group label="Boyutlama (kâr sermayeye eklensin mi?)" :description="lockText('sizingMode', '')">
      <b-row>
        <b-col v-for="(info, key) in sizing" :key="key" md="4" class="mb-1">
          <div
            class="border rounded p-1 h-100"
            :class="[value.sizingMode === key ? `border-${info.variant} bg-light-${info.variant}` : '', isLocked('sizingMode') ? '' : 'cursor-pointer']"
            @click="!isLocked('sizingMode') && set('sizingMode', key)"
          >
            <div class="d-flex justify-content-between align-items-center">
              <strong>{{ info.label }}</strong>
              <b-badge :variant="`light-${info.variant}`">{{ info.badge }}</b-badge>
            </div>
            <small class="text-muted">{{ info.desc }}</small>
          </div>
        </b-col>
      </b-row>
    </b-form-group>

    <b-row v-if="showMode">
      <b-col md="6">
        <b-form-group label="Mod" :description="lockText('mode', 'TESTNET: kağıt işlem, canlı alış/satış fiyatından dolum. LIVE: gerçek emir.')">
          <b-form-radio-group
            :checked="value.mode"
            buttons
            button-variant="outline-primary"
            :disabled="isLocked('mode')"
            :options="[{ text: 'TESTNET (kağıt)', value: 'TESTNET' }, { text: 'LIVE (gerçek)', value: 'LIVE', disabled: !liveEnabled }]"
            @change="onMode"
          />
          <div v-if="!liveEnabled"><small class="text-muted">LIVE sunucuda kapalı (FLEET_LIVE_ENABLED).</small></div>
        </b-form-group>
      </b-col>
      <b-col v-if="value.mode === 'LIVE'" md="6">
        <b-form-group label="API anahtarı" description="Yalnız Binance veya Binance Testnet anahtarları.">
          <b-form-select :value="value.apiKeyId" :disabled="isLocked('mode')" :options="apiKeyOptions" @change="set('apiKeyId', $event)" />
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BFormGroup, BFormInput, BButton, BBadge, BFormRadioGroup, BFormSelect,
} from 'bootstrap-vue'
import { SIZING_INFO } from '../fleetFormat'

export default {
  name: 'FleetRiskForm',
  components: {
    BRow, BCol, BFormGroup, BFormInput, BButton, BBadge, BFormRadioGroup, BFormSelect,
  },
  props: {
    value: { type: Object, required: true },
    // field -> reason text; a field present here is read-only
    locked: { type: Object, default: () => ({}) },
    apiKeys: { type: Array, default: () => [] },
    liveEnabled: { type: Boolean, default: false },
    showMode: { type: Boolean, default: true },
  },
  data() {
    return { leverages: [1, 2, 3, 4, 5, 6, 7], sizing: SIZING_INFO }
  },
  computed: {
    perSlot() {
      const n = Number(this.value.budget) / Math.max(1, Number(this.value.slotCount) || 1)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
    apiKeyOptions() {
      const usable = this.apiKeys.filter(k => k.provider && ['BINANCE', 'BINANCE_TESTNET'].includes(k.provider.kind))
      return [{ value: null, text: usable.length ? 'Seç…' : 'Uygun API anahtarı yok' }]
        .concat(usable.map(k => ({ value: k.id, text: `${k.label || `#${k.id}`} · ${k.provider.kind === 'BINANCE_TESTNET' ? 'Binance Testnet' : 'Binance'}${k.isActive ? '' : ' (pasif)'}`, disabled: !k.isActive })))
    },
  },
  methods: {
    set(field, v) { this.$emit('input', { ...this.value, [field]: v }) },
    setNum(field, v) { this.set(field, v === '' ? null : Number(v)) },
    setInt(field, v) { this.set(field, v === '' ? null : parseInt(v, 10)) },
    onMode(v) { this.$emit('input', { ...this.value, mode: v, apiKeyId: v === 'LIVE' ? this.value.apiKeyId : null }) },
    isLocked(field) { return Object.prototype.hasOwnProperty.call(this.locked, field) },
    lockText(field, normal) { return this.isLocked(field) ? `Kilitli: ${this.locked[field]}` : normal },
    leverageVariant(l) { if (l <= 4) return 'success'; return l === 5 ? 'warning' : 'danger' },
  },
}
</script>

<style scoped>
.cursor-pointer { cursor: pointer; }
</style>
